import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { OnChangeValue } from 'react-select';
import { inviteToCaseModal } from 'src/components/pages/Case/Case.slice';
import { inviteToCase } from 'src/components/pages/Case/actions/inviteToCase';
import { useInviteToCaseMutation } from 'src/graphql';
import { AppDispatch } from 'src/store';
import { CasePermissionsType } from 'src/types';
import { InviteUserDropdownOption } from '../DropdownSelect';
import { StepperModal } from '../StepperModal';
import { PeopleStep, SummaryStep } from './Steps';
import { AccessStep } from './Steps/AccessStep';

interface InviteToCaseModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

enum InviteSteps {
  PEOPLE,
  ACCESS,
  SUMMARY,
}

type FormState = {
  activeStep: InviteSteps;
  selectedTeamMembers: InviteUserDropdownOption[];
  note: string;
  permissions: CasePermissionsType[];
};

const DEFAULT_STATE = {
  selectedTeamMembers: [],
  note: '',
  permissions: [],
  activeStep: InviteSteps.PEOPLE,
};

export const InviteToCaseModal = ({
  open,
  setOpen,
}: InviteToCaseModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const { availableTeamMembers, caseId } = useSelector(inviteToCaseModal);
  const [formState, setFormState] = useState<FormState>(DEFAULT_STATE);
  const [buttonState, setButtonState] = useState<'default' | 'pending'>(
    'default',
  );

  useEffect(() => {
    if (
      formState.activeStep === InviteSteps.ACCESS &&
      formState.selectedTeamMembers.length === 0
    ) {
      setFormState((prevState) => ({
        ...prevState,
        activeStep: InviteSteps.PEOPLE,
        permissions: [],
      }));
    }
  }, [formState.activeStep, formState.selectedTeamMembers]);

  const handleSelectTeamMembers = (
    optionsSelected: OnChangeValue<InviteUserDropdownOption, boolean>,
  ) => {
    let filteredOptionsSelected = optionsSelected;
    if (Array.isArray(optionsSelected)) {
      filteredOptionsSelected = optionsSelected?.filter(
        (option: InviteUserDropdownOption) => !option.teamMember,
      );
    }
    setFormState((prevState) => ({
      ...prevState,
      selectedTeamMembers:
        filteredOptionsSelected as InviteUserDropdownOption[],
    }));
  };

  const handleEditNote = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormState((prevState) => ({
      ...prevState,
      note: e.target.value,
    }));
  };

  const handleSetPermissions = (
    permissions: CasePermissionsType[],
    action: 'add' | 'remove',
  ) => {
    if (action === 'add') {
      setFormState((prevState) => ({
        ...prevState,
        permissions: [...new Set([...prevState.permissions, ...permissions])],
      }));
    } else {
      setFormState((prevState) => ({
        ...prevState,
        permissions: prevState.permissions.filter(
          (permission) => !permissions.includes(permission),
        ),
      }));
    }
  };

  const handleBackButtonClick = () => {
    setFormState((prevState) => ({
      ...prevState,
      activeStep: Math.max(prevState.activeStep - 1, 0),
    }));
  };

  const inviteToCaseMutation = useInviteToCaseMutation();

  const selectedUserIDs = formState.selectedTeamMembers.map((caseManger) => {
    return caseManger.value as number;
  });

  const handleSubmitSuccess = () => {
    setFormState(DEFAULT_STATE);
    setOpen(false);
  };

  const handleContinueButtonClick = () => {
    if (formState.activeStep === InviteSteps.SUMMARY) {
      dispatch(
        inviteToCase({
          caseId: caseId,
          entityPermissions: formState.permissions,
          userIds: selectedUserIDs,
          note: formState.note,
          mutationInviteToCase: inviteToCaseMutation.mutateAsync,
          queryClient,
          setButtonState,
          handleSubmitSuccess,
        }),
      );
    } else {
      setFormState((prevState) => ({
        ...prevState,
        activeStep: Math.min(
          prevState.activeStep + 1,
          Object.keys(InviteSteps).length - 1,
        ),
      }));
    }
  };

  const steps = [
    {
      label: 'People',
      component: (
        <PeopleStep
          availableTeamMembers={availableTeamMembers}
          selectedTeamMembers={formState.selectedTeamMembers}
          handleSelectTeamMembers={handleSelectTeamMembers}
        />
      ),
    },
    {
      label: 'Access',
      component: (
        <AccessStep
          selectedTeamMembers={formState.selectedTeamMembers}
          permissions={formState.permissions}
          handleSetPermissions={handleSetPermissions}
          handleSelectTeamMembers={handleSelectTeamMembers}
        />
      ),
    },
    {
      label: 'Summary',
      component: (
        <SummaryStep
          selectedTeamMembers={formState.selectedTeamMembers}
          note={formState.note}
          handleNoteChange={handleEditNote}
          selectedCaseAccess={formState.permissions}
        />
      ),
    },
  ];
  const noCaseManagersSelected =
    formState.activeStep === InviteSteps.PEOPLE &&
    formState.selectedTeamMembers.length === 0;
  const noPermissionsSelected =
    formState.activeStep === InviteSteps.ACCESS &&
    formState.permissions.length === 0;
  const isContinueButtonDisabled =
    noCaseManagersSelected || noPermissionsSelected;

  return (
    <StepperModal
      open={open}
      setOpen={setOpen}
      steps={steps}
      activeStep={formState.activeStep}
      data-testid="invite-to-case-stepper-modal"
      isContinueButtonDisabled={isContinueButtonDisabled}
      finalStepContinueButtonText="Send invite"
      onCloseModal={() => setFormState(DEFAULT_STATE)}
      onBackButtonClick={handleBackButtonClick}
      onContinueButtonClick={handleContinueButtonClick}
      submitButtonState={buttonState}
    />
  );
};
